/* eslint-disable react/no-unknown-property */
import { nodeNumberToGoTo } from '../../../stores/utils/node-number';
import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  Dropdown,
  Icon,
  Menu,
  MenuDivider,
  MenuItem,
} from '@pypestream/design-system';
import { Tenancy_Node_Type_Enum_Ps_Enum } from '@pypestream/api-services';
import { NodeColumns, tableStore } from '../../../stores/table-store';
import { isGroupRow, isSubRow } from '../../../stores/utils/group-row-guards';
import { buildInsertNodesLabel } from '../../../stores/utils/insert-node-label';
import {
  buildRowContextMenuLayout,
  resolveIsSingleRowSelection,
  RowContextMenuEntry,
} from '../../../stores/utils/row-context-menu-visibility';

export interface RowContextMenuProps {
  /** Row the menu acts on (also the group id when the row is a group header). */
  rowId: string;
  /** Element id the dropdown anchors to (the right-clicked cell / group header). */
  triggerId: string;
  /** Stable index — only used for the dropdown wrapper test-id (kept for parity). */
  rowIndex: number;
  /** Whether the dropdown is open. */
  active: boolean;
  /** Caller maps this to its own 'closing' state transition. */
  onRequestClose: () => void;
}

const RowContextMenuComponent: React.FC<RowContextMenuProps> = ({
  rowId,
  triggerId,
  rowIndex,
  active,
  onRequestClose,
}) => {
  const rowNodeType = tableStore.getValue(rowId, NodeColumns.nodeType);
  const rowSettings = tableStore.getValue(rowId, NodeColumns.settings);
  // Canonical narrowing guards (PSTUDIO-3202): the inlined copies that had to
  // be hand-kept byte-identical to group-row-guards are gone now that the
  // guards are in main. `isMemberRow` avoids colliding with the `isSubRow`
  // import.
  const isGroupNode = isGroupRow({ nodeType: rowNodeType });
  const isMemberRow = isSubRow({ settings: rowSettings });
  const rowIntent = tableStore.getValue(rowId, NodeColumns.intent);
  const rowNodeNumber = tableStore.getValue(rowId, NodeColumns.nodeNumber);

  // PSTUDIO-4229: one definition, shared with `draggable-row.tsx` and the pure
  // `resolveRowPasteTargets` — see `stores/utils/continuous-multi-selection.ts`.
  const isPartOfMultiSelection =
    tableStore.isRowInContinuousMultiSelection(rowId);

  // PSTUDIO-4229: the primary Paste acts on the whole selection, and a replace
  // pastes onto its TOP-MOST VISIBLE row — so the item's enabled state has to be
  // judged against THAT row, not against the row the menu was opened on. A
  // continuous selection can cross a node group's boundary, and the group-in-
  // group rule (`resolvePasteGroupId`) answers per row; the store applies the
  // same anchor rule to the gesture itself (`pasteRowsReplacingRowsFromSystem
  // OrMemory`), so the menu and the store cannot disagree about which row is
  // judged.
  const pasteReplaceGateRowId = isPartOfMultiSelection
    ? (tableStore.resolveReplacePasteAnchorRowId([
        ...tableStore.selectedRows.values(),
      ]) ?? rowId)
    : rowId;

  // PSTUDIO-2872: the menu inserts as many nodes as there are selected data
  // rows; a lone right-click (no multi-selection) inserts 1.
  const selectedDataRowCount = [...tableStore.selectedRows.values()].filter(
    (id) =>
      tableStore.getRowById(id)?.nodeType !== Tenancy_Node_Type_Enum_Ps_Enum.G
  ).length;
  const insertCount = isPartOfMultiSelection
    ? Math.max(1, selectedDataRowCount)
    : 1;

  // PSTUDIO-3848: with a continuous multi-selection the insert must anchor to
  // the BOUNDARY of the whole selection (top-most for 'above', bottom-most for
  // 'below'), not the row the menu was triggered from — otherwise the block
  // lands around the trigger and ignores the rest of the selection. A lone
  // right-click keeps anchoring to its own row.
  const insertAnchorRowId = (position: 'above' | 'below'): string =>
    isPartOfMultiSelection
      ? (tableStore.getInsertionAnchorRowId(position) ?? rowId)
      : rowId;

  const close = () => onRequestClose();

  // Ordered entries (items + dividers) come from one pure, unit-tested source
  // so the rendered menu and the divider-composition test can never drift.
  const layout = buildRowContextMenuLayout({
    isGroupNode,
    isSubRow: isMemberRow,
    isPartOfMultiSelection,
    isSingleRowSelection: resolveIsSingleRowSelection({
      selectedCount: tableStore.selectedRows.size,
      isRowSelected: tableStore.isSelectedRow(rowId),
    }),
    canGroupSelection: tableStore.canGroupSelection,
    hasSearchQuery: !!tableStore.searchQuery,
    hasRowNumber: nodeNumberToGoTo(rowNodeNumber) !== undefined,
  });

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!tableStore.canDeleteRow(rowId)) return;
    // Snapshot the target rows BEFORE close(): closing the menu returns focus
    // and clears tableStore.selectedRows, so reading it inside the deferred
    // timer would see an empty set and delete nothing (deleteRows([]) early-
    // returns). The 50ms defer avoids a removeChild during the popover close;
    // the snapshot keeps the deletion target stable across it.
    const idsToDelete = [...tableStore.selectedRows.values()];
    close();
    setTimeout(() => {
      if (isGroupNode) {
        // PSTUDIO-3163: cascade header + every member. A plain
        // deleteRows([groupId]) removes only the header and orphans the
        // subRows (dangling groupId) — _deleteRowsInternal reconciles parents
        // only when a *member* is deleted, never when the header id is passed.
        tableStore.deleteRowGroup(rowId);
      } else {
        tableStore.deleteRows(idsToDelete);
      }
    }, 50);
  };

  const renderEntry = (entry: RowContextMenuEntry, index: number) => {
    switch (entry) {
      case 'divider':
        return <MenuDivider key={`divider-${index}`} />;
      case 'go-to-node':
        return (
          <MenuItem
            key="go-to-node"
            test-id="c-editor-table-row-context-menu-go-to-node"
            onClick={(e) => {
              e.preventDefault();
              close();
              const goTo = nodeNumberToGoTo(rowNodeNumber);
              if (goTo) tableStore.goToNode(goTo);
            }}
          >
            <Icon size="small" name="arrow-circle-broken-right" slot="prefix" />
            Go to node
          </MenuItem>
        );
      case 'copy':
        return (
          <MenuItem
            key="copy"
            test-id="c-editor-table-row-context-menu-copy"
            onClick={(e) => {
              e.preventDefault();
              tableStore.copySelectedRows();
              close();
            }}
          >
            <Icon size="small" name="copy" slot="prefix" />
            Copy
          </MenuItem>
        );
      case 'cut':
        return (
          <MenuItem
            key="cut"
            test-id="c-editor-table-row-context-menu-cut"
            onClick={(e) => {
              e.preventDefault();
              tableStore.cutSelectedRows();
              close();
            }}
          >
            <Icon size="small" name="scissors" slot="prefix" />
            Cut
          </MenuItem>
        );
      case 'paste-above':
        return (
          <MenuItem
            key="paste-above"
            test-id="c-editor-table-row-context-menu-paste-above"
            onClick={(e) => {
              e.preventDefault();
              // PSTUDIO-3547: SYSTEM-first so "last copy wins" across tabs /
              // solutions (fire-and-forget; menu closes synchronously below).
              void tableStore.pasteRowFromSystemOrMemory(rowId, 'above');
              close();
            }}
            disabled={!tableStore.canPasteRowAt(rowId, 'above')}
          >
            <Icon size="small" name="paste-above" slot="prefix" />
            Paste above
          </MenuItem>
        );
      case 'paste-replace':
        return (
          <MenuItem
            key="paste-replace"
            test-id="c-editor-table-row-context-menu-paste-replace"
            onClick={(e) => {
              e.preventDefault();
              // PSTUDIO-4229: "Paste" over a multi-row selection replaces the
              // WHOLE selection, as the keyboard Cmd/Ctrl+V does — the menu used
              // to hand the store the ONE clicked row, so one of the selected
              // rows survived. WHICH rows the gesture acts on is decided by the
              // store (`pasteFromRowContextMenu` → `resolveRowPasteTargets`),
              // not here: a branch written in this file could only ever be
              // unit-tested by a copy of itself.
              //
              // The selection is SNAPSHOTTED before `close()`, because the store
              // entrance is async (it awaits the system-clipboard refresh — SYSTEM
              // -first, guard rail 10), so anything read off
              // `tableStore.selectedRows` after this point is whatever the
              // selection has become — the same reason `handleDelete` snapshots.
              const selectedRowIds = [...tableStore.selectedRows.values()];
              const { isSelectionContinuous } = tableStore;
              close();
              // Fire-and-forget, as the other paste items are.
              void tableStore.pasteFromRowContextMenu(
                rowId,
                selectedRowIds,
                isSelectionContinuous
              );
            }}
            disabled={
              !tableStore.canPasteRowAt(pasteReplaceGateRowId, 'replace')
            }
          >
            <Icon size="small" name="clipboard" slot="prefix" />
            Paste
          </MenuItem>
        );
      case 'paste-below':
        return (
          <MenuItem
            key="paste-below"
            test-id="c-editor-table-row-context-menu-paste-below"
            onClick={(e) => {
              e.preventDefault();
              // PSTUDIO-3547: SYSTEM-first so "last copy wins" across tabs /
              // solutions (fire-and-forget; menu closes synchronously below).
              void tableStore.pasteRowFromSystemOrMemory(rowId, 'below');
              close();
            }}
            disabled={!tableStore.canPasteRowAt(rowId, 'below')}
          >
            <Icon size="small" name="paste-below" slot="prefix" />
            Paste below
          </MenuItem>
        );
      case 'insert-above':
        return (
          <MenuItem
            key="insert-above"
            test-id="c-editor-table-row-context-menu-insert-above"
            onClick={(e) => {
              e.preventDefault();
              tableStore.insertRowsSmart(
                insertAnchorRowId('above'),
                'above',
                insertCount
              );
              close();
            }}
          >
            <Icon size="small" name="arrow-up" slot="prefix" />
            {buildInsertNodesLabel(insertCount, 'above')}
          </MenuItem>
        );
      case 'insert-below':
        return (
          <MenuItem
            key="insert-below"
            test-id="c-editor-table-row-context-menu-insert-below"
            onClick={(e) => {
              e.preventDefault();
              tableStore.insertRowsSmart(
                insertAnchorRowId('below'),
                'below',
                insertCount
              );
              close();
            }}
          >
            <Icon size="small" name="arrow-down" slot="prefix" />
            {buildInsertNodesLabel(insertCount, 'below')}
          </MenuItem>
        );
      case 'nl-entry':
        return (
          <MenuItem
            key="nl-entry"
            test-id="c-editor-table-row-context-menu-nl-entry"
            onClick={(e) => {
              e.preventDefault();
              close();
              tableStore.toggleOutOfScopeIntent(rowId);
            }}
          >
            <Icon size="small" name="stars" slot="prefix" />
            {rowIntent === 'out_of_scope'
              ? 'Clear as NL Entry node'
              : 'Set as NL entry node'}
          </MenuItem>
        );
      case 'group':
        return (
          <MenuItem
            key="group"
            test-id="c-editor-table-row-context-menu-group"
            onClick={(e) => {
              e.preventDefault();
              tableStore.groupRows([...tableStore.selectedRows.values()]);
              close();
            }}
          >
            <Icon size="small" name="microagent" slot="prefix" />
            Group Nodes
          </MenuItem>
        );
      case 'ungroup':
        return (
          <MenuItem
            key="ungroup"
            test-id="c-editor-table-row-context-menu-ungroup"
            onClick={(e) => {
              e.preventDefault();
              const targetGroupId = isGroupNode ? rowId : rowSettings?.groupId;
              if (targetGroupId) {
                tableStore.ungroupRows(targetGroupId);
              }
              close();
            }}
          >
            <Icon size="small" name="unlink" slot="prefix" />
            Ungroup Nodes
          </MenuItem>
        );
      case 'clear':
        return (
          <MenuItem
            key="clear"
            test-id="c-editor-table-row-context-menu-clear"
            onClick={(e) => {
              e.preventDefault();
              tableStore.clearRows([...tableStore.selectedRows.values()]);
              close();
            }}
          >
            <Icon size="small" name="eraser" slot="prefix" />
            Clear
          </MenuItem>
        );
      case 'delete':
        return (
          <MenuItem
            key="delete"
            onClick={handleDelete}
            disabled={!tableStore.canDeleteRow(rowId)}
            title={
              tableStore.canDeleteRow(rowId) ? '' : 'Cannot delete last row'
            }
            test-id="c-editor-table-row-context-menu-delete"
          >
            <Icon size="small" name="delete" slot="prefix" />
            Delete
          </MenuItem>
        );
      default: {
        // Compile-time exhaustiveness: if a new RowContextMenuEntry is added to
        // the union without a case here, `entry` is no longer `never` and this
        // line fails the build — instead of silently dropping the item.
        const exhaustiveCheck: never = entry;
        return exhaustiveCheck;
      }
    }
  };

  return (
    <Dropdown
      trigger={triggerId}
      test-id={`c-editor-table--indexCell-td-${rowIndex}-dropdown`}
      // PSTUDIO-1433: the popover id must be UNIQUE PER ROW — still true, but
      // since PSTUDIO-4229 for a different reason than the one written here
      // before. `ps-popover` creates ONE `ps-portal-destination` per id and
      // projects itself into it, so a shared id makes every row's menu share a
      // single portal slot and the portal's `replaceChildren` evicts another
      // row's still-open dropdown. Nothing puts the evicted node back: a
      // popover re-appends itself to its original parent only when IT closes
      // (`handleProjectContent(false)` from its own `updated()` in
      // `popover-base.wc.ts`), and the eviction is not a close — so until then
      // the host is detached and React's `removeChild` on unmount runs against
      // the wrong parent: `NotFoundError`, the whole page to the error
      // boundary. (What is NO
      // longer part of this story is the stack: since PSTUDIO-4229 the close
      // branch un-projects AND deactivates the focus trap unconditionally,
      // not only for the top of `popoverItemsSignal`. What still reads that
      // stack is `handleClickOutside`, which dismisses only the popover that is
      // `at(-1)`.) A per-row id also stops two `<td>`s answering the same
      // selector, which is what a stuck duplicate row node in a full e2e run
      // turned out to be. The cell menu (`cell-value-context-menu.tsx`) already
      // derives a per-cell id; this is the same fix for the row menu.
      id={`c-editor-table--indexCell-${triggerId}-dropdown`}
      active={active}
      disableTriggerListeners
      // PSTUDIO-4033: insert/paste from this menu reveals the new block, and the
      // trap's deferred focus return would scroll the trigger back into view and
      // undo that reveal. The opt-out covers that return only — moving around
      // inside the open menu still scrolls normally, which matters because this
      // menu (up to 12 items) DOES outgrow the dropdown's `max-height: 60vh`.
      preventScroll
      onclose={close}
    >
      <Menu>{layout.map((entry, index) => renderEntry(entry, index))}</Menu>
    </Dropdown>
  );
};

export const RowContextMenu = observer(RowContextMenuComponent);
